/**
 * Typed client methods for the BEAM estimate-builder surface.
 *
 * Routes (beam-backend billingRouter.ts / domainRouter.ts):
 *   POST   /api/estimates                       { CreateEstimateRequest }  → EstimateDetail
 *   POST   /api/estimates/:id/lines             { AddLineItemRequest }     → EstimateDetail
 *   PATCH  /api/estimates/:id/lines/:lineId     { UpdateLineItemRequest }  → EstimateDetail
 *   DELETE /api/estimates/:id/lines/:lineId                                → EstimateDetail
 *   POST   /api/estimates/:id/discount          { SetEstimateDiscountRequest } → EstimateDetail
 *   POST   /api/estimates/:id/convert-to-job                               → (job ref)
 *   POST   /api/estimates/:id/invoice                                      → (invoice ref)
 *   GET    /api/customers                                                  → CustomerDto[]
 *   GET    /api/tax-rates                                                  → TaxRateDto[]
 *   GET    /api/unit-of-measure                                            → UnitOfMeasureDto[]
 *
 * (listEstimates / getEstimate / transitionEstimate live in src/api/billing.ts.)
 *
 * All shapes from @beam/api-types — never re-declared here. Responses parsed with the
 * zod schemas so the app fails loudly on contract drift (like billing.ts).
 * Bearer token attached by src/api/client.ts. No PII in URLs (CLAUDE.md §2).
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { get, post, patch, del } from './client';
import {
  EstimateDetail,
  TaxRateDto,
  CustomerDto,
  UnitOfMeasureDto,
} from '@beam/api-types';
import type {
  CreateEstimateRequest,
  AddLineItemRequest,
  UpdateLineItemRequest,
  SetEstimateDiscountRequest,
} from '@beam/api-types';

// ---------------------------------------------------------------------------
// Estimate builder mutations — all return the recomputed EstimateDetail
// ---------------------------------------------------------------------------

/** Create a new estimate (draft). customerId required; markup/tax/context optional. */
export async function createEstimate(
  body: CreateEstimateRequest,
): Promise<EstimateDetail> {
  const raw = await post<unknown>('/api/estimates', body);
  return EstimateDetail.parse(raw);
}

/** Append a line item to an estimate; server recomputes totals. */
export async function addLineItem(
  estimateId: string,
  body: AddLineItemRequest,
): Promise<EstimateDetail> {
  const raw = await post<unknown>(`/api/estimates/${estimateId}/lines`, body);
  return EstimateDetail.parse(raw);
}

/** Patch an existing line item (subset of fields). */
export async function updateLineItem(
  estimateId: string,
  lineId: string,
  body: UpdateLineItemRequest,
): Promise<EstimateDetail> {
  const raw = await patch<unknown>(
    `/api/estimates/${estimateId}/lines/${lineId}`,
    body,
  );
  return EstimateDetail.parse(raw);
}

/** Remove a line item; server recomputes totals. */
export async function deleteLineItem(
  estimateId: string,
  lineId: string,
): Promise<EstimateDetail> {
  const raw = await del<unknown>(`/api/estimates/${estimateId}/lines/${lineId}`);
  return EstimateDetail.parse(raw);
}

/** Set (or clear, with discount:null) the primary in-app discount. */
export async function setEstimateDiscount(
  estimateId: string,
  body: SetEstimateDiscountRequest,
): Promise<EstimateDetail> {
  const raw = await post<unknown>(`/api/estimates/${estimateId}/discount`, body);
  return EstimateDetail.parse(raw);
}

/**
 * Convert an approved/signed estimate into a job (owner action, server-enforced).
 * Returns the new (or existing — conversion is idempotent server-side) job id so the
 * caller can navigate to it. The backend returns the raw job row, so read `id` defensively.
 */
export async function convertEstimateToJob(estimateId: string): Promise<{ jobId: string | null }> {
  const raw = await post<unknown>(`/api/estimates/${estimateId}/convert-to-job`);
  const id = (raw as { id?: unknown } | null)?.id;
  return { jobId: typeof id === 'string' ? id : null };
}

/**
 * Create an invoice from an estimate (owner action, server-enforced).
 * Returns an invoice reference; caller refreshes the invoices list on success.
 */
export async function createInvoiceFromEstimate(
  estimateId: string,
): Promise<unknown> {
  return post<unknown>(`/api/estimates/${estimateId}/invoice`);
}

// ---------------------------------------------------------------------------
// Reference lists for the builder forms
// ---------------------------------------------------------------------------

/** Customers for the estimate customer picker. */
export async function listCustomers(): Promise<CustomerDto[]> {
  const raw = await get<unknown>('/api/customers');
  return (raw as unknown[]).map((item) => CustomerDto.parse(item));
}

/** Tax rates for the estimate tax picker. */
export async function listTaxRates(): Promise<TaxRateDto[]> {
  const raw = await get<unknown>('/api/tax-rates');
  return (raw as unknown[]).map((item) => TaxRateDto.parse(item));
}

/** Units of measure (global catalog). */
export async function listUnitsOfMeasure(): Promise<UnitOfMeasureDto[]> {
  const raw = await get<unknown>('/api/unit-of-measure');
  return (raw as unknown[]).map((item) => UnitOfMeasureDto.parse(item));
}
